export type FinancePeriod = { id: string; sourceId?: string; label: string; endDate: string; status: 'open' | 'closed' | 'unknown' };
export type Observation =
  | { status: 'measured' | 'reconciling' | 'defaulted'; amount: number; note: string }
  | { status: 'unavailable'; amount: null; note: string };
export type Summary = (Observation | { status: 'mixed'; amount: number; note: string }) & {
  contributions: { period: FinancePeriod; observation: Observation }[];
};
export type StatementRow = {
  id: string; label: string; emphasis?: boolean; values: Record<string, Observation>;
  accounts?: Record<string, AccountContribution[]>;
};
export type AccountContribution = {
  id: string; glCode: string; accountName: string; accountType: string; amount: number; adjustment?: boolean;
};
export type Statement = {
  id: string; title: string; basis: string; entity: string; currency: 'USD' | 'GBP';
  kind: 'flow' | 'closing'; rows: StatementRow[];
};
export type Reconciliation = {
  id: string; title: string; status: 'matched' | 'variance' | 'unavailable';
  difference: number | null; owner: string; note: string;
};
export const OBSERVATION_LABELS = {
  measured: 'Measured', reconciling: 'Reconciling item', defaulted: 'Defaulted', unavailable: 'Could not measure',
  mixed: 'Mixed',
};

/** Caller supplies chronologically ordered periods; no baked thirteen-month calendar. */
export function selectedPeriods(periods: FinancePeriod[], anchorId: string, count: number): FinancePeriod[] {
  const index = periods.findIndex((p) => p.id === anchorId);
  if (index < 0 || !Number.isSafeInteger(count) || count < 1) return [];
  return periods.slice(Math.max(0, index - count + 1), index + 1);
}

// Display aggregation only, not a replacement for the report's accounting API.
// Closing balances take the final column; missing flow inputs never become zero.
export function periodSummary(row: StatementRow, periods: FinancePeriod[], kind: Statement['kind']): Summary {
  const contributions = (kind === 'closing' ? periods.slice(-1) : periods).map((period) => ({
    period, observation: usableObservation(row.values[period.id]),
  }));
  const inputs = contributions.map((item) => item.observation);
  if (!inputs.length || inputs.some((v) => v.status === 'unavailable')) {
    return { status: 'unavailable', amount: null, note: 'One or more required values could not be measured.', contributions };
  }
  // No precedence hides a contributor. Mixed is an aggregate, not a fifth source state.
  const statuses = new Set(inputs.map((v) => v.status));
  const status = statuses.size > 1 ? 'mixed' : inputs[0].status;
  const amount = inputs.reduce((sum, v) => sum + (v.amount as number), 0);
  if (!Number.isFinite(amount)) return { status: 'unavailable', amount: null, note: 'The display total is not finite.', contributions };
  // The unavailable branch above has already returned.
  if (status === 'unavailable') throw new Error('unreachable unavailable summary');
  return { status, amount, contributions, note: kind === 'closing' ? 'Closing balance at the last selected period.' : 'Sum of the selected periods; all contributing states are retained below.' };
}

export function usableObservation(value: Observation | undefined): Observation {
  return !value || value.status === 'unavailable' || !Number.isFinite(value.amount)
    ? { status: 'unavailable', amount: null, note: value?.note || 'No finite observation supplied.' }
    : value;
}

export function formatAmount(amount: number | null, unit: number): string {
  if (amount === null || !Number.isFinite(amount)) return OBSERVATION_LABELS.unavailable;
  if (![1, 1000, 1000000].includes(unit)) throw new Error('unsupported display unit');
  const digits = unit === 1 ? 0 : 2;
  const magnitude = Math.abs(amount / unit);
  // Disclose any nonzero magnitude below a display increment, not a rounded zero.
  const increment = unit === 1 ? 1 : 0.01;
  const text = amount !== 0 && magnitude < increment ? (unit === 1 ? '<1' : '<0.01')
    : magnitude.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return amount < 0 ? `(${text})` : text;
}

/** Account contributions for the periods represented by a clicked statement value. */
export function accountDetailsFor(row: StatementRow, periods: FinancePeriod[]): AccountContribution[] {
  const totals = new Map<string, AccountContribution>();
  for (const period of periods) for (const account of row.accounts?.[period.id] ?? []) {
    const current = totals.get(account.id);
    if (current) current.amount += account.amount;
    else totals.set(account.id, { ...account });
  }
  return [...totals.values()].sort((a, b) => a.glCode.localeCompare(b.glCode, undefined, { numeric: true })
    || a.accountName.localeCompare(b.accountName) || Number(!!a.adjustment) - Number(!!b.adjustment));
}
